import { etagFromRow, get, getVersioned, post, postVersioned, putVersioned, seg } from '../client';
import type { ListParams, Page } from '../types/common';
import type { SafetyFinding } from '../types/orders';
import type {
  Administration, AllergyListResponse, AllergyRecord, CorrectAdministrationRequest, CreateAdministrationRequest, CreateAllergyRequest,
  CreateMedicationStatementRequest, CreatePrescriptionRequest, CreateReconciliationRequest, MedicationConcept, MedicationItem, MedicationReconciliation,
  Pharmacy, Prescription, PrescriptionCheckResult, RefillDecisionRequest, RefillRequest, SendPrescriptionRequest, SignPrescriptionRequest,
  UpdateReconciliationRequest,
} from '../types/meds';

/** `W/"<rowVersion>"` for a backend aggregate (falls back to the mock's `version`). */
export const rowTag = (r: { rowVersion?: number | null; version?: number | null } | null | undefined): string | null =>
  etagFromRow(r?.rowVersion ?? r?.version ?? null);

const withTag = <T extends { rowVersion?: number }>(r: { data: T; etag: string | null }) => ({ data: r.data, etag: r.etag ?? etagFromRow(r.data?.rowVersion) });

/** Drop `status=all` (backend filters on the literal value; omitting it means "all"). */
const noAll = <P extends { status?: string }>(p: P): P => (p.status === 'all' ? { ...p, status: undefined } : p);

/** POST /prescriptions/{id}:check returns findings under `cards` (order :check uses `findings`) — accept both. */
export function normalizePrescriptionCheck(r: Partial<PrescriptionCheckResult> & { cards?: SafetyFinding[] }): PrescriptionCheckResult {
  return {
    evaluationId: r.evaluationId,
    evaluatedAt: r.evaluatedAt ?? new Date().toISOString(),
    findings: r.findings ?? r.cards ?? [],
    deterministic: r.deterministic,
  };
}

/** Defaults for older (mock) reconciliation items: no sourceKind means a home-medication statement. */
export function normalizeReconciliation(r: MedicationReconciliation): MedicationReconciliation {
  if (!r?.items) return r;
  return { ...r, items: r.items.map((i) => ({ ...i, sourceKind: i.sourceKind ?? (i.medicationItemId ? 'statement' : i.sourceKind) })) };
}
const reconV = (r: { data: MedicationReconciliation; etag: string | null }) => {
  const t = withTag(r);
  return { ...t, data: normalizeReconciliation(t.data) };
};

export const medsApi = {
  search: (q: string) => get<Page<MedicationConcept>>('/medications/search', { q }),
  list: (patientId: string, p: ListParams & { status?: string } = {}) => get<Page<MedicationItem>>(`/patients/${seg(patientId)}/medications`, { ...noAll(p) }),
  addStatement: (patientId: string, body: CreateMedicationStatementRequest) =>
    post<MedicationItem>(`/patients/${seg(patientId)}/medication-statements`, body, { idempotent: true }),
};

export const allergiesApi = {
  list: (patientId: string) => get<AllergyListResponse>(`/patients/${seg(patientId)}/allergies`),
  /** NKA / NKDA / NOT_ASSESSED / unknown are assertions, not absence (ALG-001). */
  create: (patientId: string, body: CreateAllergyRequest) => post<AllergyRecord>(`/patients/${seg(patientId)}/allergies`, body, { idempotent: true }),
  /** If-Match: the allergy row version (pass the row; `W/"<rowVersion>"`). */
  enteredInError: (id: string, reason: string, etag: string | null = null) =>
    post<AllergyRecord>(`/allergies/${seg(id)}:entered-in-error`, { reason }, { ifMatch: etag, idempotent: true }),
};

export const reconciliationApi = {
  start: async (body: CreateReconciliationRequest) => normalizeReconciliation(await post<MedicationReconciliation>('/medication-reconciliations', body, { idempotent: true })),
  save: async (id: string, body: UpdateReconciliationRequest, etag: string | null) =>
    reconV(await putVersioned<MedicationReconciliation>(`/medication-reconciliations/${seg(id)}`, body, { ifMatch: etag })),
  complete: async (id: string, etag: string | null) =>
    normalizeReconciliation(await post<MedicationReconciliation>(`/medication-reconciliations/${seg(id)}:complete`, {}, { ifMatch: etag, idempotent: true })),
  /** Start (or resume the in-progress review for the encounter), with the ETag needed for autosave (If-Match). */
  begin: async (body: CreateReconciliationRequest) => reconV(await postVersioned<MedicationReconciliation>('/medication-reconciliations', body, { idempotent: true })),
  get: async (id: string) => reconV(await getVersioned<MedicationReconciliation>(`/medication-reconciliations/${seg(id)}`)),
  /** Reviews for a patient, newest first (worklist "meds reviewed" status). */
  list: async (patientId: string, p: ListParams & { encounterId?: string } = {}) => {
    const page = await get<Page<MedicationReconciliation>>('/medication-reconciliations', { patientId, ...p });
    return { ...page, items: page.items.map(normalizeReconciliation) };
  },
};

export const prescriptionsApi = {
  list: (patientId: string) => get<Page<Prescription>>('/prescriptions', { patientId }),
  create: (body: CreatePrescriptionRequest) => post<Prescription>('/prescriptions', body, { idempotent: true }),
  get: async (id: string) => withTag(await getVersioned<Prescription>(`/prescriptions/${seg(id)}`)),
  /** Deterministic safety evaluation for signing (read-only; no If-Match). */
  check: async (id: string) => normalizePrescriptionCheck(await post(`/prescriptions/${seg(id)}:check`, {})),
  /**
   * Step-up. Body carries a structured override for every non-info finding from :check (422 ETNAS_SAFETY_CHECK with
   * problem.findings otherwise). If-Match: the prescription version.
   */
  sign: (id: string, label: string, body: SignPrescriptionRequest = {}, etag: string | null = null) =>
    post<Prescription>(`/prescriptions/${seg(id)}:sign`, body, { ifMatch: etag, idempotent: true, stepUpLabel: label }),
  send: (id: string, body: SendPrescriptionRequest = {}, etag: string | null = null) =>
    post<Prescription>(`/prescriptions/${seg(id)}:send`, body, { ifMatch: etag, idempotent: true }),
  cancel: (id: string, reason: string, etag: string | null = null) =>
    post<Prescription>(`/prescriptions/${seg(id)}:cancel`, { reason }, { ifMatch: etag, idempotent: true }),
  /** Prescriptions written by the caller across patients (worklist). */
  mine: (p: ListParams & { status?: string } = {}) => get<Page<Prescription>>('/prescriptions', { mine: true, limit: 100, ...noAll(p) }),
  /** Pharmacy directory with sandbox partner coverage. */
  pharmacies: () => get<Page<Pharmacy>>('/pharmacies'),
};

export const refillsApi = {
  list: (p: ListParams & { status?: string } = {}) => get<Page<RefillRequest>>('/refill-requests', { ...noAll(p) }),
  get: async (id: string) => withTag(await getVersioned<RefillRequest>(`/refill-requests/${seg(id)}`)),
  /**
   * Step-up. Backend requires `reason` and `quantity`; it creates a linked DRAFT prescription that the prescriber
   * must still sign and send (nothing is transmitted by approving). If-Match: the refill request version.
   */
  approve: (id: string, body: RefillDecisionRequest, label: string, etag: string | null = null) =>
    post<RefillRequest>(`/refill-requests/${seg(id)}:approve`, body, { ifMatch: etag, idempotent: true, stepUpLabel: label }),
  deny: (id: string, body: RefillDecisionRequest, etag: string | null = null) =>
    post<RefillRequest>(`/refill-requests/${seg(id)}:deny`, body, { ifMatch: etag, idempotent: true }),
};

export const administrationsApi = {
  record: (body: CreateAdministrationRequest) => post<Administration>('/administrations', administrationBody(body), { idempotent: true }),
  list: (patientId: string) => get<Page<Administration>>('/administrations', { patientId }),
  get: (id: string) => get<Administration>(`/administrations/${seg(id)}`),
  /** Records a new administration that supersedes `id` (the original stays, linked). Only the latest version can be corrected. */
  correct: (id: string, body: CorrectAdministrationRequest) => post<Administration>(`/administrations/${seg(id)}:correct`, { ...body, administration: administrationBody(body.administration) }, { idempotent: true }),
};

/** Not-given evidence must not fabricate a lot, route, site or expiration. */
export function administrationBody(body: CreateAdministrationRequest) {
  return body.status === 'not_given'
    ? { ...body, route: body.route || undefined, site: body.site || undefined, lot: body.lot || undefined, expiration: body.expiration || undefined }
    : body;
}
